import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Receipt, 
  CreditCard, 
  Calendar, 
  Download, 
  Filter,
  Loader,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/config/api';

interface BillingItem {
  type: 'subscription' | 'payment';
  id: number;
  amount: number;
  tier_id?: string;
  billing_cycle?: string;
  status?: string;
  payment_method?: string;
  date?: string;
  description: string;
}

interface Invoice {
  id: number;
  order_id: number;
  total: number;
  material_component?: number;
  stitching_component?: number;
  issued_at?: string;
  receipts: Array<{
    id: number;
    receipt_number: string;
    amount: number;
    created_at?: string;
  }>;
}

const BillingHistoryPage: React.FC = () => {
  const { user, token } = useAuth();
  const [billingHistory, setBillingHistory] = useState<BillingItem[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'history' | 'invoices'>('history');
  const [filter, setFilter] = useState<'all' | 'subscription' | 'payment'>('all');

  useEffect(() => {
    if (activeTab === 'history') {
      fetchBillingHistory();
    } else {
      fetchInvoices();
    }
  }, [activeTab]);

  const fetchBillingHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/billing/history?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch billing history');
      }

      const data = await response.json();
      setBillingHistory(data.billing_history || []);
      setError(null);
    } catch (_err) {
      console.error('Error fetching billing history:', err);
      setError('Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/billing/invoices?limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      setInvoices(data.invoices || []);
      setError(null);
    } catch (_err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'failed':
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const filteredHistory = billingHistory.filter(item => {
    if (filter === 'all') return true;
    return item.type === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-gray-600">Loading billing history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Billing History</h1>
          <p className="text-gray-600">View your subscription payments, order payments, and invoices</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Payment History
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'invoices'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Invoices
            </button>
          </nav>
        </div>

        {/* Payment History Tab */}
        {activeTab === 'history' && (
          <div>
            {/* Filter */}
            <div className="mb-6 flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <div className="flex space-x-2">
                {(['all', 'subscription', 'payment'] as const).map((filterType) => (
                  <button
                    key={filterType}
                    onClick={() => setFilter(filterType)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filter === filterType
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                  >
                    {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Billing History List */}
            {filteredHistory.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Billing History</h3>
                <p className="text-gray-600">Your payment history will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredHistory.map((item) => (
                  <motion.div
                    key={`${item.type}-${item.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          item.type === 'subscription' 
                            ? 'bg-purple-100 text-purple-600' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {item.type === 'subscription' ? (
                            <CreditCard className="w-6 h-6" />
                          ) : (
                            <Receipt className="w-6 h-6" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {item.description}
                            </h3>
                            {item.status && getStatusIcon(item.status)}
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(item.date)}</span>
                            </div>
                            {item.payment_method && (
                              <span className="capitalize">{item.payment_method}</span>
                            )}
                            {item.billing_cycle && (
                              <span className="capitalize">{item.billing_cycle}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 mb-1">
                          {formatCurrency(item.amount)}
                        </div>
                        {item.status && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            item.status.toLowerCase() === 'completed' || item.status.toLowerCase() === 'active'
                              ? 'bg-green-100 text-green-700'
                              : item.status.toLowerCase() === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {item.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            {invoices.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Invoices</h3>
                <p className="text-gray-600">Your invoices will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <motion.div
                    key={invoice.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Invoice #{invoice.id}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Order #{invoice.order_id} • {formatDate(invoice.issued_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatCurrency(invoice.total)}
                        </div>
                      </div>
                    </div>
                    
                    {invoice.material_component && invoice.stitching_component && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Material:</span>
                            <span className="ml-2 font-semibold text-gray-900">
                              {formatCurrency(invoice.material_component)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Stitching:</span>
                            <span className="ml-2 font-semibold text-gray-900">
                              {formatCurrency(invoice.stitching_component)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {invoice.receipts && invoice.receipts.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Receipts</h4>
                        <div className="space-y-2">
                          {invoice.receipts.map((receipt) => (
                            <div key={receipt.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center space-x-2">
                                <Receipt className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-700">#{receipt.receipt_number}</span>
                                <span className="text-gray-500">
                                  {formatDate(receipt.created_at)}
                                </span>
                              </div>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency(receipt.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-4 flex justify-end">
                      <button className="inline-flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Download className="w-4 h-4" />
                        <span>Download Invoice</span>
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingHistoryPage;

